import { AlertCircle, CheckCircle2, Circle } from 'lucide-react'
import type { RulesEngineRunValidation } from '../../types/rulesEngine'
import { useTheme } from '../../contexts/ThemeContext'

type RulesEngineValidationPanelProps = {
  validation: RulesEngineRunValidation
  showErrors: boolean
  /** Hide the patient step when the patient is already fixed (Patient Detail page). */
  hidePatientCheck?: boolean
}

type CheckItem = {
  key: keyof Pick<RulesEngineRunValidation, 'patient' | 'ruleSet' | 'rules' | 'benefits'>
  label: string
}

const CHECKS: CheckItem[] = [
  { key: 'patient', label: 'Patient selected' },
  { key: 'ruleSet', label: 'Rule set selected' },
  { key: 'rules', label: 'Rule set has rules' },
  { key: 'benefits', label: 'Benefit inputs ready' },
]

export default function RulesEngineValidationPanel({
  validation,
  showErrors,
  hidePatientCheck = false,
}: RulesEngineValidationPanelProps) {
  const { theme } = useTheme()
  const checks = hidePatientCheck ? CHECKS.filter((c) => c.key !== 'patient') : CHECKS

  return (
    <div
      className="rounded-2xl border p-4"
      style={{
        borderColor: showErrors && !validation.valid ? '#fecaca' : theme.colors.cardBorder,
        backgroundColor: showErrors && !validation.valid ? '#fef2f2' : theme.colors.cardBackground,
      }}
    >
      <div className="flex items-center gap-2">
        {validation.valid ? (
          <CheckCircle2 className="h-4 w-4 text-teal-600" />
        ) : (
          <AlertCircle
            className="h-4 w-4"
            style={{ color: showErrors ? '#dc2626' : theme.colors.textMuted }}
          />
        )}
        <h3 className="text-sm font-bold" style={{ color: theme.colors.textPrimary }}>
          {validation.valid ? 'Ready to run' : 'Before you run'}
        </h3>
      </div>
      <p className="mt-1 text-xs" style={{ color: theme.colors.textSecondary }}>
        {validation.valid
          ? 'All checks passed. You can run the rules engine.'
          : 'Complete each step below to run the rules engine.'}
      </p>

      <ul className="mt-3 space-y-2">
        {checks.map(({ key, label }) => {
          const error = validation[key]
          const passed = !error
          const showFail = showErrors && !passed

          return (
            <li
              key={key}
              className="flex items-start gap-2 rounded-lg px-2 py-1.5"
              style={{
                backgroundColor: showFail ? '#fff' : 'transparent',
              }}
            >
              {passed ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" />
              ) : showFail ? (
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              ) : (
                <Circle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: theme.colors.textMuted }} />
              )}
              <div className="min-w-0">
                <p
                  className="text-xs font-semibold"
                  style={{ color: passed ? theme.colors.textPrimary : showFail ? '#dc2626' : theme.colors.textSecondary }}
                >
                  {label}
                </p>
                {error ? (
                  <p
                    className="mt-0.5 text-[11px]"
                    style={{ color: showFail ? '#b91c1c' : theme.colors.textMuted }}
                  >
                    {error}
                  </p>
                ) : null}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
